import Color from '../Color/Color'
import Paragraph from '../Paragraph/Paragraph'
import PopOut from '../PopOut/PopOut'
import Tooltip from '../Tooltip/Tooltip'
import classes from './RemoveableChip.module.scss'
import { IoClose } from 'react-icons/io5'
import { type ReactNode, useState } from 'react'

export interface RemoveableProp {
  onRemove: () => void
  removeLabel: string
}
export interface NonRemoveableProp {
  onRemove?: never
  removeLabel?: never
}

export interface BaseProps {
  label: string
  children?: ReactNode
  isRemoveable?: boolean
}

type Props = BaseProps & (RemoveableProp | NonRemoveableProp)

const RemoveableChip = ({
  label,
  onRemove,
  removeLabel,
  children,
  isRemoveable = false
}: Props) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    if (isFocused) {
      setIsHovered(true)

      return
    }

    setIsHovered(false)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
    setIsHovered(false)
  }

  const isHoverClass = isHovered
    ? classes.hover
    : ''

  const isFocusClass = isFocused
    ? classes.focus
    : ''

  const getRemoveableChipClass = () => [
    classes.RemoveableChip,
    isHoverClass,
    isFocusClass
  ].filter(classStyle => classStyle).join(' ')

  const labelButtonClass = !isRemoveable
    ? classes.singleLabelButton
    : classes.labelButton

  const triggerInfoButton = (ref: any, onClick: () => void, isOpen: boolean | undefined) => {
    if (isOpen) {
      handleFocus()
    }

    return (
      <button
        className={getRemoveableChipClass() + ' ' + labelButtonClass}
        onBlur={handleBlur}
        onClick={onClick}
        onFocus={handleFocus}
        ref={ref}
        type='button'
      >
        <Paragraph variant='text-overflow'>
          <Tooltip label={label}>
            <small>
              <Color
                display='inline'
                variant='theme-description-text'
              >
                {label}
              </Color>
            </small>
          </Tooltip>
        </Paragraph>
      </button>
    )
  }

  return (
    <div
      aria-label='RemoveableChip'
      className={getRemoveableChipClass()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <PopOut
        isPopOutAlign
        placement='bottom-start'
        trigger={triggerInfoButton}
      >
        {children}
      </PopOut>
      {
        isRemoveable &&
          <Tooltip label={removeLabel}>
            <button
              aria-label={`Remove ${label}`}
              className={getRemoveableChipClass() + ' ' + classes.iconButton}
              onBlur={handleBlur}
              onClick={onRemove}
              onFocus={handleFocus}
              type='button'
            >
              <Color variant='theme-remove-chip'>
                <IoClose
                  aria-hidden
                  size={14}
                />
              </Color>
            </button>
          </Tooltip>
      }
    </div>
  )
}

export default RemoveableChip
